// ============================================================================
// BOQ — library item code generation
// ============================================================================
// A new item's code is derived from its classification rather than typed:
//
//   TRADE     the short_code on the chosen trade_group        (2 letters)
//   SUBTRADE  the short_code on the chosen brio_trade sub-trade (3 letters)
//   SEQUENCE  the lowest number in the chosen trade type's series that no item
//             holds (4 digits), or in the sub-trade's own range when no trade
//             type sits under it (the same rule as boq.next_item_code, which
//             numbers a split and a move to Purge)
//
// Pure, so the modal, the duplicate path and the tests all run the same rule.
// It never reads the database: callers pass the items already in memory.
//
// An existing item is renumbered only when its classification changes in the
// Edit modal AND its current code no longer fits the new classification
// (itemCodeFitsClassification below). Codes minted before this existed keep
// whatever they were given until then.
// ============================================================================

// Exactly what boq.items accepts, and what parseItemCode in useBoqLibrary
// expects to read back.
const TRADE_RE = /^[A-Z]{2}$/;
const SUBTRADE_RE = /^[A-Z]{3}$/;

// Purge: the holding area items are moved into while they are sorted. Its
// codes are OO.OO.0001 onwards (letter O), it carries no classification and
// sits under no chapter. It is recognised by the prefix alone, the same test
// as boq.is_purge_code.
export const PURGE_PREFIX = 'OO.OO.';
export const PURGE_LABEL = 'Purge';
export const isPurgeCode = (code) => /^OO\.OO\.\d{4}$/.test(code || '');

export const formatItemCode = (trade, subtrade, sequence) => (
  `${trade}.${subtrade}.${String(sequence).padStart(4, '0')}`
);

// A series bound is shown as the code segment it will become, so a series
// entered as 1 to 100 reads 0001 to 0100. Display only: series_start and
// series_end stay plain integers in the column, and Number() reads a padded
// string back to the same integer.
export const formatSeriesNumber = (n) => (
  n === null || n === undefined || n === '' ? '' : String(n).padStart(4, '0')
);

export const formatSeriesRange = (from, to) => (
  from === null || from === undefined || to === null || to === undefined
    ? '' : `${formatSeriesNumber(from)}–${formatSeriesNumber(to)}`
);

/**
 * Work out the next code for a new item.
 *
 * @param tradeGroup  {shortCode, label}  — the trade_group node, or null
 * @param subtrade    {shortCode, label}  — the brio_trade sub-trade node, or null
 * @param subtrade    {shortCode, label, seriesStart, seriesEnd} — the brio_trade sub-trade node, or null
 * @param tradeType   {seriesStart, seriesEnd, label} — the trade_type node, or null
 * @param subtradeHasTypes  whether any trade type is anchored under `subtrade`
 * @param items       [{ trade, subtrade, sequence }] — every item in the library,
 *                    archived ones included (an archived item still owns its code)
 * @returns { code } on success, or { error } when something is missing or
 *          every number in the series is held.
 *
 * A sub-trade with no trade types under it numbers its items from its own
 * range, so the trade type is only required where one exists to pick. Where
 * types do exist the sub-trade's range has been split between them, and a
 * number drawn from the whole range could land in any type's band, so one of
 * them has to be chosen.
 */
export function generateItemCode({
  tradeGroup, subtrade, tradeType, subtradeHasTypes = true, items = [],
}) {
  if (!tradeGroup) return { error: 'Pick a trade group.' };
  if (!subtrade) return { error: 'Pick a chapter / sub-trade.' };
  if (!tradeType && subtradeHasTypes) return { error: 'Pick a trade type — it supplies the item number.' };

  const trade = (tradeGroup.shortCode || '').toUpperCase();
  const sub = (subtrade.shortCode || '').toUpperCase();

  if (!trade) {
    return { error: `Trade group "${tradeGroup.label}" has no code name yet. Set one on the Taxonomies page.` };
  }
  if (!TRADE_RE.test(trade)) {
    return { error: `Trade group "${tradeGroup.label}" has a ${trade.length}-letter code name (${trade}); the first segment must be exactly 2 letters.` };
  }
  if (!sub) {
    return { error: `Sub-trade "${subtrade.label}" has no code name yet. Set one on the Taxonomies page.` };
  }
  if (!SUBTRADE_RE.test(sub)) {
    return { error: `Sub-trade "${subtrade.label}" has a ${sub.length}-letter code name (${sub}); the second segment must be exactly 3 letters.` };
  }

  // The band the number is drawn from: the trade type's series, or the
  // sub-trade's own range when it has no types to split it between.
  const owner = tradeType
    ? { from: tradeType.seriesStart, to: tradeType.seriesEnd, noun: 'Trade type', label: tradeType.label }
    : { from: subtrade.seriesStart, to: subtrade.seriesEnd, noun: 'Sub-trade', label: subtrade.label };
  const { from, to } = owner;
  if (from == null || to == null) {
    return {
      error: tradeType
        ? `Trade type "${tradeType.label}" has no series yet. Set one on the Taxonomies page.`
        : `Sub-trade "${subtrade.label}" has no trade types and no range yet. Set its range, or add a trade type, on the Taxonomies page.`,
    };
  }

  // The lowest number in this series that no item holds. A code given up by
  // a renumber, a merge or a move to Purge holds nothing, so emptying a type
  // frees every number in it. Mirrors boq.next_item_code, so New item,
  // Duplicate, Split and the Edit modal number the same way.
  const held = new Set();
  items.forEach((i) => {
    if (i.trade !== trade || i.subtrade !== sub) return;
    const n = Number(i.sequence);
    if (Number.isInteger(n)) held.add(n);
  });
  let next = null;
  for (let n = from; n <= to; n += 1) {
    if (!held.has(n)) { next = n; break; }
  }
  if (next === null) {
    return { error: `Series ${formatSeriesRange(from, to)} on ${owner.noun.toLowerCase()} "${owner.label}" is full: every number in it is held. Widen it on the Taxonomies page.` };
  }

  return { code: formatItemCode(trade, sub, next) };
}

/**
 * Whether an existing code already reads as the classification chosen for it:
 * the right TRADE and SUBTRADE segments, and a number inside the band the
 * classification draws from. When it does, reclassifying keeps the code, so an
 * item is renumbered only when its old code would misdescribe it.
 *
 * Takes the same classification arguments as generateItemCode.
 */
export function itemCodeFitsClassification({
  itemCode, tradeGroup, subtrade, tradeType, subtradeHasTypes = true,
}) {
  const parts = /^([A-Z]{2})\.([A-Z]{3})\.(\d{4})$/.exec(itemCode || '');
  if (!parts || !tradeGroup || !subtrade) return false;
  if (!tradeType && subtradeHasTypes) return false;
  if (parts[1] !== (tradeGroup.shortCode || '').toUpperCase()) return false;
  if (parts[2] !== (subtrade.shortCode || '').toUpperCase()) return false;
  const from = tradeType ? tradeType.seriesStart : subtrade.seriesStart;
  const to = tradeType ? tradeType.seriesEnd : subtrade.seriesEnd;
  if (from == null || to == null) return false;
  const n = parseInt(parts[3], 10);
  return n >= from && n <= to;
}
